import React, { useState } from 'react'
import { Button, Flex, Input } from '../../../Components'
import { useParams, useNavigate } from 'react-router-dom'
import { saveEnvironment, testEmailConfig, useEnvironment, EMAIL_PROVIDER_OPTIONS, getEmailConfigDisplayValue, applyMicrosoftEmailDefaults } from './Functions'
import { PageContainer } from '../../../Components/Layout/PageContainer'
import { Text } from '../../../Components/Common/Text'
import { Card } from '../../../Components/Common/Card'
import { ImagePicker } from '../../../Components/Common/ImagePicker'
import { FC } from '../../../Services'
import { Color } from '../../../Components/Common/ColorPicker'

const defaultStyle = { width: '31%', marginRight: '1%', marginLeft: '1%', marginBottom: 15 }

const sections = [{
  title: 'Dati Generali',
  items: [
    { location: '_id', label: 'ID' },
    { location: 'ssoKey', label: 'SSO Key' },
    { location: 'codSociety', label: 'Codice Società' },
    { location: 'showUserAtlInTab', label: 'Mostra operatore', dropdown: [{ label: 'Sì', value: true }, { label: 'No', value: false }], defaultValue: false }
  ]
},
{
  title: 'Design',
  items: [
    { location: 'design.name', label: 'Nome' },
    { location: 'design.links.support', label: 'Supporto' },
    { location: 'design.links.contacts', label: 'Contatti' },
    { location: 'design.mainColor', label: 'Main Color', color: true },
    { location: 'design.logo', label: 'Logo', image: true }
  ]
},
{
  title: 'Tema App',
  items: [
    { location: 'appTheme.colors.white', label: 'White', color: true },
    { location: 'appTheme.colors.black', label: 'Black', color: true },
    { location: 'appTheme.colors.grey', label: 'Grey', color: true },
    { location: 'appTheme.colors.themeColor', label: 'Theme Color', color: true },
    { location: 'appTheme.colors.fontColor', label: 'Font Color', color: true },
    { location: 'appTheme.colors.textButtonColor', label: 'Text Button Color', color: true },
    { location: 'appTheme.logoImage', label: 'Immagine Logo / Sfondo App', image: true }
  ]
}
]

const defaultColors = { translucent: 'rgba(255, 255, 255, 0.55)', opaque: 'rgba(255, 255, 255, 0.8)', transparent: 'rgba(0, 0, 0, 0)', darkTransparent: 'rgba(0,0,0,0.5)' }

const getValueLocation = (location, obj) => {
  const keys = location.split('.')
  return keys.reduce((acc, key) => acc?.[key], obj)
}

const MICROSOFT_DEFAULT_FIELDS = ['configEmail.auth.user', 'configEmail.host', 'configEmail.port']

const EmailConfigSection = ({ environment, setEnvironment, smtpLog, setSmtpLog }) => {
  const provider = getValueLocation('configEmail.provider', environment) || 'smtp'
  const isMicrosoft = provider === 'microsoft_oauth'
  const configEmail = environment?.configEmail

  const getFieldValue = (location) => {
    if (isMicrosoft && MICROSOFT_DEFAULT_FIELDS.includes(location)) {
      return getEmailConfigDisplayValue(location, configEmail)
    }
    return getValueLocation(location, environment) ?? ''
  }

  const renderField = (location, label, options = {}) => (
    <Input
      key={location}
      id={location}
      value={getFieldValue(location)}
      onChange={({ [location]: value }) => setEnvironment(location, value)}
      label={label}
      style={defaultStyle}
      {...options}
    />
  )

  return (
    <Flex fw style={{ width: '100%', marginBottom: 20 }}>
      <Text center value='Configurazione Email' title bold size={20} style={{ width: '100%', marginBottom: 20 }} />

      <Flex fw row style={{ width: '100%', marginBottom: 15 }}>
        <Input
          key='configEmail.provider'
          id='configEmail.provider'
          value={provider}
          onChange={({ 'configEmail.provider': value }) => setEnvironment('configEmail.provider', value)}
          label='Tipo invio email'
          style={{ ...defaultStyle, width: '31%' }}
          dropdown
          options={EMAIL_PROVIDER_OPTIONS}
        />
      </Flex>

      <Flex fw wrap row>
        {renderField('configEmail.from', 'Mittente (from)')}
        {renderField('configEmail.fromLabel', 'Scritta Mittente')}
        {renderField('configEmail.subject', 'Oggetto Email')}
        {renderField('configEmail.auth.user', isMicrosoft ? 'Mailbox SMTP' : 'Username')}
        {renderField('configEmail.host', isMicrosoft ? 'Host SMTP' : 'Host')}
        {renderField('configEmail.port', 'Porta')}

        {isMicrosoft ? (
          <>
            {renderField('configEmail.oauth.tenantId', 'Tenant ID')}
            {renderField('configEmail.oauth.clientId', 'Client ID')}
            {renderField('configEmail.oauth.clientSecret', 'Client Secret')}
            {renderField('configEmail.oauth.refreshToken', 'Refresh Token')}
          </>
        ) : (
          <>
            {renderField('configEmail.auth.pass', 'Password')}
            <Input
              key='configEmail.secure'
              id='configEmail.secure'
              value={getValueLocation('configEmail.secure', environment)}
              onChange={({ 'configEmail.secure': value }) => setEnvironment('configEmail.secure', value)}
              label='Secure'
              style={defaultStyle}
              dropdown
              options={[{ label: 'Si', value: true }, { label: 'No', value: false }]}
            />
          </>
        )}
      </Flex>

      <Text center value='Test invio email' title bold size={18} style={{ width: '100%', marginTop: 16, marginBottom: 12 }} />
      <Text
        value='La mail di test viene inviata all’indirizzo sotto, oppure al mittente (from) se lasci vuoto.'
        size={14}
        style={{ width: '100%', marginBottom: 12, textAlign: 'center' }}
      />
      <Flex fw style={{ marginBottom: 10 }}>
        <Flex row alignCenter style={{ width: '100%', marginBottom: 10, gap: '1em' }}>
          <Input
            key='configEmail.to'
            id='configEmail.to'
            value={getValueLocation('configEmail.to', environment) ?? ''}
            onChange={({ 'configEmail.to': value }) => setEnvironment('configEmail.to', value)}
            label='Email destinatario (test)'
            style={{ width: '40%' }}
          />
        </Flex>
        <Button
          label='Invia email di test'
          icon='send'
          onClick={() => testEmailConfig(applyMicrosoftEmailDefaults(environment?.configEmail), getValueLocation('configEmail.to', environment), setSmtpLog)}
        />
        {smtpLog && (
          <Flex center style={{ width: '100%' }}>
            <pre
              style={{
                background: '#f7f7f7',
                padding: 10,
                borderRadius: 30,
                marginTop: 10,
                whiteSpace: 'pre-wrap',
                width: '70%',
                textAlign: 'center'
              }}
            >
              {smtpLog}
            </pre>
          </Flex>
        )}
      </Flex>
    </Flex>
  )
}

export const EnvEditor = () => {
  const [smtpLog, setSmtpLog] = useState('')
  const { environmentId } = useParams()
  const [environment, setEnvironment, isSuccess] = useEnvironment(environmentId)
  const navigate = useNavigate()

  const isNew = environmentId === 'new'

  const save = async () => {
    let elaboratedEnvironment = { ...environment }

    if (elaboratedEnvironment.configEmail?.to) {
      delete elaboratedEnvironment.configEmail.to
    }

    if (elaboratedEnvironment.configEmail) {
      elaboratedEnvironment.configEmail = applyMicrosoftEmailDefaults(elaboratedEnvironment.configEmail)
    }

    const siapResult = await FC.service('environments').find({ query: { _id: 'siap' } })
    const siapMobyt = siapResult?.[0]?.mobyt || {}

    elaboratedEnvironment.mobyt = { ...elaboratedEnvironment.mobyt, ...siapMobyt }
    elaboratedEnvironment.hasFtoSign = false
    elaboratedEnvironment.alwaysUseOtp = true
    elaboratedEnvironment.hasEstemporaneo = true
    elaboratedEnvironment.showUserAtlInTab = elaboratedEnvironment.showUserAtlInTab ?? false
    elaboratedEnvironment = {
      ...elaboratedEnvironment,
      appTheme: { colors: { ...defaultColors, ...elaboratedEnvironment.appTheme?.colors }, ...elaboratedEnvironment.appTheme }
    }

    const res = await saveEnvironment(elaboratedEnvironment, isNew)
    res?._id && navigate('/environments/' + res._id)
    return res
  }

  if (!isSuccess || !environment) return null

  return (
    <PageContainer style={{ marginTop: -60 }}>
      <Card>
        <Flex fw>
          <Text value={`${isNew ? 'Crea' : 'Modifica'} Environment`} title bold size={30} style={{ marginBottom: 20 }} />
          <Flex fw wrap row>
            {sections.map(({ title, items }) => (
              <Flex fw key={title} style={{ width: '100%', marginBottom: 20 }}>
                <Text center value={title} title bold size={20} style={{ width: '100%', marginBottom: 20 }} />
                <Flex fw wrap row>
                  {items.map(({ location, label, dropdown, image, color, defaultValue }) => {
                    if (color) {
                      return (
                        <Color
                          value={[getValueLocation(location, environment), (value) => setEnvironment(location, value)]}
                          label={label}
                          key={location}
                        />
                      )
                    }
                    if (image) {
                      return (
                        <ImagePicker
                          key={location}
                          style={{ width: '100%' }}
                          label={label}
                          image={[getValueLocation(location, environment), (value) => setEnvironment(location, value)]}
                        />
                      )
                    }
                    if (dropdown) {
                      const rawValue = getValueLocation(location, environment)
                      const value = rawValue !== undefined ? rawValue : (defaultValue ?? null)
                      return (
                        <Input
                          key={location}
                          id={location}
                          value={value}
                          onChange={({ [location]: v }) => setEnvironment(location, v)}
                          label={label}
                          style={defaultStyle}
                          dropdown
                          options={dropdown}
                        />
                      )
                    }
                    return (
                      <Input
                        key={location}
                        id={location}
                        value={getValueLocation(location, environment)}
                        onChange={({ [location]: value }) => setEnvironment(location, value)}
                        label={label}
                        style={defaultStyle}
                      />
                    )
                  })}
                </Flex>
              </Flex>
            ))}

            <EmailConfigSection
              environment={environment}
              setEnvironment={setEnvironment}
              smtpLog={smtpLog}
              setSmtpLog={setSmtpLog}
            />

            <Flex row>
              <Button label={isNew ? 'Crea' : 'Salva'} icon='plus' style={{ marginTop: 20 }} onClick={save} />
              <Button label='Indietro' icon='close' style={{ marginTop: 20, marginLeft: 10 }} onClick={() => window.history.back()} />
            </Flex>
          </Flex>
        </Flex>
      </Card>
    </PageContainer>
  )
}
